import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields, AGGREGATION_TYPES } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function RadialBarConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray.filter((f) => f.aggregatable),
        allFields: fieldsArray,
      }
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const radialBarConfig = config.radialBarConfig || { nameKey: '', valueKey: '', aggregation: 'SUM' as const }
  const isValid = Boolean(
    config.metadata?.title &&
    radialBarConfig.nameKey &&
    radialBarConfig.valueKey &&
    radialBarConfig.aggregation
  )

  const COLOR_PALETTES = [
    { value: 'blue-gradient', label: 'Azules', colors: ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd'] },
    { value: 'green-gradient', label: 'Verdes', colors: ['#15803d', '#22c55e', '#4ade80', '#86efac'] },
    { value: 'purple-gradient', label: 'Púrpuras', colors: ['#6b21a8', '#a855f7', '#c084fc', '#e9d5ff'] },
    { value: 'mixed', label: 'Multicolor', colors: ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'] },
  ]

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Barras Radiales</h2>
      <p className="text-gray-600 mb-6">
        Visualiza progreso o comparación en formato circular
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Objetivos de Conservación por Provincia"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo de nombre/categoría */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Nombre/Categoría <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={radialBarConfig.nameKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                radialBarConfig: { ...radialBarConfig, nameKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Etiquetas para cada barra radial (ej: provincia, especie, objetivo)
          </p>
        </div>

        {/* Campo de valor */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Valor <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={radialBarConfig.valueKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                radialBarConfig: { ...radialBarConfig, valueKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Valor numérico que determinará el tamaño de cada barra
          </p>
        </div>

        {/* Tipo de agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación de Agregación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-4 text-center transition-all
                  ${
                    (radialBarConfig.aggregation || 'SUM') === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    radialBarConfig: { ...radialBarConfig, aggregation: agg.value as any },
                  })
                }
              >
                <div className="text-2xl mb-1">{agg.icon}</div>
                <div className="font-medium text-sm">{agg.value}</div>
                <div className="text-xs text-gray-500 mt-1">{agg.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Valor máximo */}
        <div>
          <label className="block font-medium mb-2">Valor Máximo</label>
          <input
            type="number"
            placeholder="100"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={radialBarConfig.maxValue ?? ''}
            onChange={(e) =>
              onChange({
                ...config,
                radialBarConfig: {
                  ...radialBarConfig,
                  maxValue: e.target.value ? parseFloat(e.target.value) : undefined,
                },
              })
            }
          />
          <p className="text-sm text-gray-500 mt-1">
            💡 Deja en blanco para usar el valor máximo de los datos
          </p>
        </div>

        {/* Paleta de colores */}
        <div>
          <label className="block font-medium mb-2">Paleta de Colores</label>
          <div className="grid grid-cols-2 gap-3">
            {COLOR_PALETTES.map((palette) => (
              <button
                key={palette.value}
                type="button"
                className={`
                  border-2 rounded p-3 transition-all
                  ${
                    (radialBarConfig.colorPalette || 'blue-gradient') === palette.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    radialBarConfig: { ...radialBarConfig, colorPalette: palette.value as any },
                  })
                }
              >
                <div className="flex gap-1 mb-2">
                  {palette.colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="flex-1 h-6 rounded"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="text-sm font-medium text-center">
                  {palette.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Opciones de visualización */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showLabels"
              checked={radialBarConfig.showLabels !== false}
              onChange={(e) =>
                onChange({
                  ...config,
                  radialBarConfig: { ...radialBarConfig, showLabels: e.target.checked },
                })
              }
              className="w-5 h-5"
            />
            <label htmlFor="showLabels" className="font-medium cursor-pointer">
              Mostrar etiquetas con valores
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showPercentage"
              checked={radialBarConfig.showPercentage || false}
              onChange={(e) =>
                onChange({
                  ...config,
                  radialBarConfig: {
                    ...radialBarConfig,
                    showPercentage: e.target.checked,
                  },
                })
              }
              className="w-5 h-5"
            />
            <label htmlFor="showPercentage" className="font-medium cursor-pointer">
              Mostrar como porcentaje del máximo
            </label>
          </div>
        </div>

        {/* Radio interno */}
        <div>
          <label className="block font-medium mb-2">
            Radio Interno (0-80%)
          </label>
          <input
            type="range"
            min="0"
            max="80"
            step="10"
            className="w-full"
            value={radialBarConfig.innerRadius || 30}
            onChange={(e) =>
              onChange({
                ...config,
                radialBarConfig: {
                  ...radialBarConfig,
                  innerRadius: parseInt(e.target.value),
                },
              })
            }
          />
          <div className="flex justify-between text-sm text-gray-600 mt-1">
            <span>Lleno</span>
            <span className="font-medium">
              {radialBarConfig.innerRadius || 30}%
            </span>
            <span>Donut</span>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}
